import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  TrendingUp,
  Wallet,
  AlertTriangle,
  Clock,
  Building2,
  Loader2,
  Plus,
  FileText,
  Users,
} from "lucide-react";
import { useBillingDashboard } from "../api/dashboard";

function formatEUR(n: number, compact = false): string {
  if (compact && Math.abs(n) >= 1000) {
    return (
      new Intl.NumberFormat("es-ES", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n) + " €"
    );
  }
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(n);
}

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  accent = "emerald",
}: {
  label: string;
  value: string;
  hint?: string;
  icon: typeof Wallet;
  accent?: "emerald" | "sky" | "amber" | "rose";
}) {
  const accents = {
    emerald: "from-emerald-50 to-white border-emerald-200/60 text-emerald-700",
    sky: "from-sky-50 to-white border-sky-200/60 text-sky-700",
    amber: "from-amber-50 to-white border-amber-200/60 text-amber-700",
    rose: "from-rose-50 to-white border-rose-200/60 text-rose-700",
  } as const;
  const iconBg = {
    emerald: "bg-emerald-100 text-emerald-700",
    sky: "bg-sky-100 text-sky-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  } as const;
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br p-5 shadow-soft ${accents[accent]}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-black uppercase tracking-wider text-slate-500">
            {label}
          </div>
          <div className="mt-1 text-2xl font-black text-slate-900 tracking-tight truncate">
            {value}
          </div>
          {hint && (
            <div className="mt-1 text-xs font-semibold text-slate-500">
              {hint}
            </div>
          )}
        </div>
        <div className={`shrink-0 rounded-xl p-2.5 ${iconBg[accent]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  );
}

export function BillingDashboardPage() {
  const { data, isLoading, error } = useBillingDashboard();

  if (isLoading) {
    return (
      <div className="p-8 h-full flex items-center justify-center text-slate-500">
        <Loader2 className="animate-spin mr-2" size={20} /> Cargando dashboard…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-8">
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 p-4">
          Error al cargar el dashboard.
        </div>
      </div>
    );
  }

  const { kpis, monthly, top_clientes, por_emisor, recientes, vencidas } = data;

  // Para el gráfico de barras: max para escalar
  const maxMonthly = Math.max(...monthly.map((m) => m.total), 1);

  return (
    <div className="p-8 flex flex-col gap-6">
      {/* Header */}
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 text-slate-900">
            <BarChart3 size={28} className="text-brand-700" /> Facturación
          </h1>
          <p className="text-slate-500 mt-1">
            Vista general de tu actividad de facturación en {kpis.year}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/billing/invoices"
            className="inline-flex items-center gap-2 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 px-4 py-2.5 font-bold text-sm transition"
          >
            <FileText size={16} /> Ver todas las facturas
          </Link>
          <Link
            to="/billing/invoices/new"
            className="inline-flex items-center gap-2 rounded-xl bg-brand-700 hover:bg-brand-800 text-white px-4 py-2.5 font-bold text-sm shadow-soft transition"
          >
            <Plus size={18} /> Nueva factura
          </Link>
        </div>
      </header>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label={`Facturado ${kpis.year}`}
          value={formatEUR(kpis.total_anual)}
          hint={`Histórico total: ${formatEUR(kpis.total_global, true)}`}
          icon={TrendingUp}
          accent="emerald"
        />
        <KpiCard
          label="Facturado este mes"
          value={formatEUR(kpis.total_mensual)}
          icon={Wallet}
          accent="sky"
        />
        <KpiCard
          label="Pendiente de cobro"
          value={formatEUR(kpis.pendiente_cobro)}
          hint={`${kpis.facturas_emitidas} facturas emitidas`}
          icon={Clock}
          accent="amber"
        />
        <KpiCard
          label="Facturas vencidas"
          value={String(vencidas.length)}
          hint={
            vencidas.length > 0
              ? `${formatEUR(
                  vencidas.reduce((a, v) => a + v.total, 0),
                  true,
                )} en riesgo`
              : "Todo al día ✓"
          }
          icon={AlertTriangle}
          accent={vencidas.length > 0 ? "rose" : "emerald"}
        />
      </div>

      {/* Stats de facturas por estado */}
      <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Total facturas:
            </span>
            <span className="text-xl font-black text-slate-900">
              {kpis.facturas_total}
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-200 px-3 py-1 text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-sky-500" />
            <span className="font-bold text-sky-800">
              {kpis.facturas_emitidas}
            </span>
            <span className="text-sky-600 text-xs">emitidas</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="font-bold text-emerald-800">
              {kpis.facturas_pagadas}
            </span>
            <span className="text-emerald-600 text-xs">pagadas</span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 border border-rose-200 px-3 py-1 text-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
            <span className="font-bold text-rose-800">
              {kpis.facturas_anuladas}
            </span>
            <span className="text-rose-600 text-xs">anuladas</span>
          </span>
          {kpis.facturas_borrador > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              <span className="font-bold text-slate-700">
                {kpis.facturas_borrador}
              </span>
              <span className="text-slate-500 text-xs">borrador</span>
            </span>
          )}
        </div>
      </div>

      {/* Mensual + Por emisor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gráfico mensual */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-slate-900">
              Facturación últimos 12 meses
            </h2>
            <span className="text-xs font-bold text-slate-500">
              Máx: {formatEUR(maxMonthly, true)}
            </span>
          </div>
          {/* Bars */}
          <div className="flex items-end gap-2 h-48">
            {monthly.map((m) => {
              const heightPct =
                m.total > 0 ? Math.max((m.total / maxMonthly) * 100, 3) : 0;
              return (
                <div
                  key={m.month}
                  className="flex-1 flex flex-col items-center justify-end gap-1 h-full group"
                  title={`${m.label} ${m.month}: ${formatEUR(m.total)} · ${m.count} facturas`}
                >
                  <div className="text-[10px] font-bold text-slate-400 group-hover:text-brand-700 transition">
                    {m.total > 0 ? formatEUR(m.total, true) : ""}
                  </div>
                  <div
                    className="w-full rounded-t-md bg-gradient-to-t from-brand-700 to-brand-400 group-hover:from-brand-800 group-hover:to-brand-500 transition-all"
                    style={{ height: `${heightPct}%` }}
                  />
                </div>
              );
            })}
          </div>
          {/* Labels */}
          <div className="flex gap-2 mt-2">
            {monthly.map((m) => (
              <div
                key={`label-${m.month}`}
                className="flex-1 text-center text-[11px] font-bold text-slate-500 capitalize"
              >
                {m.label}
              </div>
            ))}
          </div>
        </div>

        {/* Por emisor */}
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
          <h2 className="font-black text-slate-900 mb-4 flex items-center gap-2">
            <Building2 size={18} className="text-brand-700" /> Por emisor
          </h2>
          <div className="space-y-3">
            {por_emisor.length === 0 && (
              <div className="text-sm text-slate-500">Sin datos.</div>
            )}
            {por_emisor.map((em) => {
              const max = Math.max(...por_emisor.map((e) => e.total), 1);
              const pct = (em.total / max) * 100;
              return (
                <div key={em.id}>
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-sm font-bold flex items-center gap-1.5 truncate"
                      style={{ color: em.color_hex ?? "#0b4a2f" }}
                    >
                      <span
                        className="w-2 h-2 rounded-full inline-block shrink-0"
                        style={{ background: em.color_hex ?? "#94a3b8" }}
                      />
                      <span className="truncate">{em.nombre}</span>
                    </span>
                    <span className="text-xs font-bold text-slate-700 ml-2">
                      {formatEUR(em.total, true)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${pct}%`,
                        background: em.color_hex ?? "#10b981",
                      }}
                    />
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {em.count} facturas
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top clientes + Vencidas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top clientes */}
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-slate-900 flex items-center gap-2">
              <Users size={18} className="text-brand-700" /> Top 5 clientes
            </h2>
            <Link
              to="/billing/clients"
              className="text-xs font-bold text-brand-700 hover:text-brand-900"
            >
              Ver todos →
            </Link>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left pb-2 font-bold">Cliente</th>
                <th className="text-right pb-2 font-bold">Facturas</th>
                <th className="text-right pb-2 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {top_clientes.length === 0 && (
                <tr>
                  <td colSpan={3} className="py-4 text-center text-slate-500">
                    Sin datos.
                  </td>
                </tr>
              )}
              {top_clientes.map((c, i) => (
                <tr key={`${c.id}-${i}`} className="border-t border-slate-100">
                  <td className="py-2.5 font-bold text-slate-900 truncate max-w-[260px]">
                    {c.nombre}
                  </td>
                  <td className="py-2.5 text-right font-mono">{c.count}</td>
                  <td className="py-2.5 text-right font-bold text-slate-900">
                    {formatEUR(c.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Vencidas */}
        <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-black text-slate-900 flex items-center gap-2">
              <AlertTriangle size={18} className="text-rose-600" /> Facturas
              vencidas
            </h2>
            <Link
              to="/billing/invoices"
              search={{ estado: "emitida" } as never}
              className="text-xs font-bold text-brand-700 hover:text-brand-900"
            >
              Ver todas →
            </Link>
          </div>
          {vencidas.length === 0 ? (
            <div className="py-8 text-center text-emerald-700">
              <div className="text-3xl mb-1">✓</div>
              <div className="text-sm font-bold">¡Sin facturas vencidas!</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left pb-2 font-bold">Nº</th>
                  <th className="text-left pb-2 font-bold">Cliente</th>
                  <th className="text-right pb-2 font-bold">Días</th>
                  <th className="text-right pb-2 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {vencidas.map((v) => (
                  <tr key={v.id} className="border-t border-slate-100">
                    <td className="py-2 font-mono font-bold text-slate-900">
                      {v.numero}
                    </td>
                    <td className="py-2 text-slate-700 truncate max-w-[220px]">
                      {v.cliente ?? "—"}
                    </td>
                    <td className="py-2 text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold">
                        {v.dias_vencida}d
                      </span>
                    </td>
                    <td className="py-2 text-right font-bold text-slate-900">
                      {formatEUR(v.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Recientes */}
      <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black text-slate-900">Últimas facturas emitidas</h2>
          <Link
            to="/billing/invoices"
            className="text-xs font-bold text-brand-700 hover:text-brand-900"
          >
            Ver todas →
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left pb-2 font-bold">Nº</th>
              <th className="text-left pb-2 font-bold">Fecha</th>
              <th className="text-left pb-2 font-bold">Emisor</th>
              <th className="text-left pb-2 font-bold">Cliente</th>
              <th className="text-left pb-2 font-bold">Estado</th>
              <th className="text-right pb-2 font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {recientes.map((f) => (
              <tr key={f.id} className="border-t border-slate-100">
                <td className="py-2 font-mono font-bold text-slate-900">
                  {f.numero}
                </td>
                <td className="py-2 text-slate-600">
                  {new Date(f.fecha).toLocaleDateString("es-ES")}
                </td>
                <td className="py-2">
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-bold"
                    style={{ color: f.emisor_color ?? undefined }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: f.emisor_color ?? "#94a3b8" }}
                    />
                    {f.emisor}
                  </span>
                </td>
                <td className="py-2 text-slate-700 truncate max-w-[280px]">
                  {f.cliente ?? "—"}
                </td>
                <td className="py-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border bg-sky-50 text-sky-700 border-sky-200">
                    {f.estado}
                  </span>
                </td>
                <td className="py-2 text-right font-bold text-slate-900">
                  {formatEUR(f.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
